import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Copy, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/api-keys/')({
  component: ApiKeysPage,
})

interface KeySummary {
  id: string
  name: string
  prefix: string
  start: string | null
  enabled: boolean
  expiresAt: string | null
  lastRequest: string | null
  createdAt: string
  revokedAt: string | null
}

function ApiKeysPage() {
  const [createOpen, setCreateOpen] = useState(false)
  const [showRevoked, setShowRevoked] = useState(false)
  const [mintedKey, setMintedKey] = useState<{ plaintext: string; name: string } | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  })

  const { data, isLoading, refetch } = useQuery(
    orpc.rbac.apiKeys.listOwn.queryOptions({ input: {} }),
  )
  const keys = (data ?? []) as KeySummary[]
  const visible = showRevoked ? keys : keys.filter((k) => !k.revokedAt)

  const createMutation = useMutation(
    orpc.rbac.apiKeys.create.mutationOptions({
      onSuccess: (result) => {
        const typed = result as { plaintext: string; key: { name: string } }
        setMintedKey({ plaintext: typed.plaintext, name: typed.key.name })
        setCreateOpen(false)
        refetch()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const revokeMutation = useMutation(
    orpc.rbac.apiKeys.revoke.mutationOptions({
      onSuccess: () => {
        toast.success('Key revoked')
        refetch()
        setDeleteDialog({ open: false, id: null })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">API Keys</h1>
          <p className="text-muted-foreground">
            Manage tokens for the public REST API. Each key inherits your permissions.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create key
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="p-6 text-muted-foreground">You haven't created any keys yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell className="font-medium">{k.name}</TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">
                      {k.start ? `${k.prefix}${k.start}…` : `${k.prefix}…`}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {k.lastRequest ? new Date(k.lastRequest).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell>
                      {k.revokedAt ? (
                        <Badge variant="outline" className="bg-red-100 text-red-800">
                          Revoked
                        </Badge>
                      ) : !k.enabled ? (
                        <Badge variant="outline" className="bg-amber-100 text-amber-800">
                          Disabled
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-green-100 text-green-800">
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!k.revokedAt && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => setDeleteDialog({ open: true, id: k.id })}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="mt-4 flex items-center gap-2">
        <input
          id="show-revoked"
          type="checkbox"
          checked={showRevoked}
          onChange={(e) => setShowRevoked(e.target.checked)}
        />
        <label htmlFor="show-revoked" className="text-sm text-muted-foreground">
          Show revoked keys
        </label>
      </div>

      <CreateKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={(data) => createMutation.mutate(data)}
        pending={createMutation.isPending}
      />

      <MintedKeyDialog mintedKey={mintedKey} onClose={() => setMintedKey(null)} />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API key?</AlertDialogTitle>
            <AlertDialogDescription>
              Any integration using this key will stop working immediately. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deleteDialog.id) revokeMutation.mutate({ id: deleteDialog.id })
              }}
              disabled={revokeMutation.isPending}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface CreateKeyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: { name: string; expiresInDays: number | null }) => void
  pending: boolean
}

function CreateKeyDialog({ open, onOpenChange, onSubmit, pending }: CreateKeyDialogProps) {
  const [name, setName] = useState('')
  const [expires, setExpires] = useState<'never' | '30' | '90' | '365'>('never')

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        if (!v) {
          setName('')
          setExpires('never')
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create API key</DialogTitle>
          <DialogDescription>
            The plaintext token is shown once. Copy it before closing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label htmlFor="api-key-name" className="text-sm font-medium">
              Name
            </label>
            <Input
              id="api-key-name"
              placeholder="e.g. Attendance sync"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="api-key-expires" className="text-sm font-medium">
              Expires
            </label>
            <Select value={expires} onValueChange={(v) => setExpires(v as typeof expires)}>
              <SelectTrigger id="api-key-expires">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Never</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="365">1 year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={!name || pending}
            onClick={() =>
              onSubmit({
                name,
                expiresInDays: expires === 'never' ? null : Number(expires),
              })
            }
          >
            {pending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface MintedKeyDialogProps {
  mintedKey: { plaintext: string; name: string } | null
  onClose: () => void
}

function MintedKeyDialog({ mintedKey, onClose }: MintedKeyDialogProps) {
  const copy = async () => {
    if (!mintedKey) return
    await navigator.clipboard.writeText(mintedKey.plaintext)
    toast.success('Copied to clipboard')
  }
  return (
    <Dialog open={mintedKey !== null} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Key created</DialogTitle>
          <DialogDescription>
            Copy this now — you won't be able to see it again. If you lose it, revoke and create a
            new one.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-muted rounded-md p-3 font-mono text-sm flex items-center gap-2 break-all">
          <span className="flex-1">{mintedKey?.plaintext}</span>
          <Button size="sm" variant="ghost" onClick={copy}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>I've copied it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
